import React from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { clearSession, getSession, isAdmin } from "../session";

// Navigation mirrors the shape of the domain, not the shape of the database:
// people, places, events.
const NAV = [
  { to: "/", label: "Overview", end: true },
  { to: "/players", label: "Players" },
  { to: "/claims", label: "Claims" },
  { to: "/clubs", label: "Clubs & courts" },
  { to: "/tournaments", label: "Tournaments" },
];

const NavItem = ({ to, label, end }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      [
        "relative block px-6 py-2.5 text-sm transition-colors",
        // The ball marks what is active. It is the only place this colour
        // appears in the chrome.
        isActive
          ? "bg-white/5 font-medium text-white before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-ball before:content-['']"
          : "text-white/60 hover:bg-white/5 hover:text-white",
      ].join(" ")
    }
  >
    {label}
  </NavLink>
);

export default function Layout() {
  const navigate = useNavigate();
  const session = getSession();

  // Guard every page behind an admin session in one place, rather than
  // repeating a check in each screen.
  if (!session) return <Navigate to="/login" replace />;
  if (!isAdmin())
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md border border-sand bg-white p-8 text-center">
          <h1 className="font-display text-xl font-bold uppercase">
            Admin access only
          </h1>
          <p className="mt-2 text-sm text-ink-soft">
            This account isn't an administrator. Sign in with an admin account
            to manage the register.
          </p>
          <button
            className="mt-6 border border-sand px-4 py-2 text-sm hover:bg-teal-wash"
            onClick={() => {
              clearSession();
              navigate("/login");
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    );

  const handleSignOut = () => {
    clearSession();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <nav className="flex shrink-0 flex-col bg-ink md:w-60">
        <div className="px-6 py-6">
          <div className="font-display text-sm font-bold uppercase leading-tight tracking-tight text-white">
            PadelRank
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-ball">
            Control room
          </div>
        </div>

        <div className="flex flex-row overflow-x-auto md:flex-col md:overflow-visible">
          {NAV.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </div>

        <div className="mt-auto border-t border-white/10 px-6 py-4">
          <div className="truncate text-xs text-white/50">{session.email}</div>
          <button
            onClick={handleSignOut}
            className="mt-2 text-xs text-white/70 underline hover:text-white"
          >
            Sign out
          </button>
        </div>
      </nav>

      <main className="min-w-0 flex-1 px-6 py-8 md:px-10 md:py-10">
        <Outlet />
      </main>
    </div>
  );
}
